import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { environment } from '@citydeals/env';

export interface OpenAiOrderItem {
  id: string | number;
  name: string;
  quantity: number;
}

/**
 * OpenAI (ChatGPT Ads) mérőpixel – CITYDEALS-719.
 * https://developers.openai.com/ads/measurement-pixel
 *
 * Csak böngészőben fut (SSR alatt nincs `window`), és csak akkor, ha az
 * environment-ben meg van adva a pixel azonosító.
 */
@Injectable({ providedIn: 'root' })
export class OpenAiTrackService {
  private readonly enabled: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: string, @Inject(DOCUMENT) private document: Document) {
    this.enabled = isPlatformBrowser(platformId) && !!environment.openAiPixelId;
  }

  /**
   * A pixel betöltése és inicializálása (APP_INITIALIZER hívja, egyszer).
   * A betöltő kód az OpenAI által adott snippet.
   */
  init(): void {
    if (!this.enabled) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w.oaiq) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const q: any = function () {
      // az SDK az eredeti snippet szerint az `arguments` objektumot várja a sorban
      // eslint-disable-next-line prefer-rest-params
      q.q.push(arguments);
    };
    q.q = [];
    w.oaiq = q;
    const js = this.document.createElement('script');
    js.async = true;
    js.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    const first = this.document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(js, first);

    // Ha később marketing hozzájáruláshoz kötjük: itt oaiq('consent', false),
    // majd a Cookiebot elfogadásakor oaiq('consent', true).
    w.oaiq('init', {
      pixelId: environment.openAiPixelId,
      debug: environment.openAiPixelDebug ?? false,
    });
  }

  /**
   * `order_created` esemény. Az `eventId` a rendelés `transaction_id`-ja,
   * ugyanaz, amit a backend a Conversions API-nak küld (deduplikáció).
   */
  trackOrderCreated(eventId: string | null | undefined, amountHuf: number, items: OpenAiOrderItem[]): void {
    // TODO: debug log, eltávolítandó
    // console.log('[OpenAI pixel] trackOrderCreated hívás', { enabled: this.enabled, eventId, amountHuf, items });
    if (!this.enabled || !eventId) {
      // console.log('[OpenAI pixel] nem küldjük: enabled =', this.enabled, ', eventId =', eventId);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const oaiq = (window as any).oaiq;
    if (typeof oaiq !== 'function') {
      // console.log('[OpenAI pixel] nem küldjük: window.oaiq nem függvény', oaiq);
      return;
    }
    const payload = {
      type: 'contents',
      // a valuta legkisebb egységében (fillér)
      amount: Math.round(amountHuf * 100),
      currency: 'HUF',
      contents: items.map(item => ({
        id: String(item.id),
        name: item.name,
        content_type: 'product',
        quantity: item.quantity,
      })),
    };
    // console.log('[OpenAI pixel] order_created küldése', payload, { event_id: eventId });
    oaiq('measure', 'order_created', payload, { event_id: eventId });
  }
}
